import React, { useEffect, useState } from 'react';
import { VIEW_TYPE } from '../../../../../enums/ViewType';
import { TABLES } from '../../../../../Apis/Tables';
import {useDispatch, useSelector} from 'react-redux';
import { TABS_FOODIE } from '../../Model/Models';
import { useParamsContextProvider } from '../../../../../hooks/ParamsContextProvider';
import {ACTION_MODEL} from '../../../../../Helpers/ActionModel';
import {permissionObj} from "../../../../../Helpers/permission";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import {DATE_TIME_FORMAT_NAME_MODEL, EMPTY_VALUES, getDateFormat} from "../../../../../Apis/functions";
import CustomChip from "../../../../../components/CustomChip";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../../enums/statusColorEnum";
import {status, STATUS_MODEL} from "../../../../../enums/statusEnum";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import {
    CancelIconsOptions,
    DeleteIconsOptions,
    DetailsIconsOptions,
    QrCodeIcon
} from "../../../../../components/buttons/IconButtons";
import {GetAccessCodeQR} from "../../../../../reduxToolkit/FoodieAccessCode/FoodieAccessCodeApi";
import QRCode from "qrcode";
import {toast} from "react-toastify";
import TableFunction from "../../../../../components/table/TableFunction";
import {SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {SimpleSearch} from "../../../../../reduxToolkit/Search/SearchApi";

/**
 * Handles the logic and state management for the Access Code module.
 * It integrates table data, modals (create, summary), search, selection, and
 * deletion/cancellation functionality for access codes.
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @param {Array<string|number>} selectForDelete - List of selected access code IDs for deletion.
 * @param {Function} setSelectForDelete - Updates the selected access codes for deletion.
 * @param {boolean} modified - Flag to track data modifications.
 * @param {Function} setModified - Updates the modified flag and triggers data reload.
 * @param {Function} setSelectForCancel - Updates the selected access codes for cancellation.
 * @param {string} option - Current model/table used for search and export operations.
 *
 * @returns {Object} Contains table configuration, state handlers, modal controls,
 * and helper functions for managing access codes.
 */
const AccessCodeTableApp = ({ selectForDelete, setSelectForDelete, modified, setModified, setSelectForCancel, option }) => {
    const dispatch = useDispatch();
    const { paramsContext, setParamsContext } = useParamsContextProvider();
    const [results, setResults] = useState([]);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [search, setSearch] = useState(false);
    const [bodyForExport, setBodyForExport] = useState('');
    const [searchElement, setSearchElement] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [itemSelected, setItemSelected] = useState(null);
    const view = VIEW_TYPE.LIST;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const tableId = "tbl_act_access_code";
    const searchId = "txt_act_search_access_code";
    const deletePermission = (DEV || permission?.includes(permissionObj?.WEB_FOODIE_ACCESS_DELETE));
    const leftPosition = deletePermission ? 50 : 0;

    /**
     * Generate table headers dynamically.
     * @returns {Array<JSX.Element>} Array of <CustomTh> header components
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes='first_head'>{t('assigned_to')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('requested_by')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('created_at')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('diner')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('start_date_at_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('end_date_at_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('status')}</CustomTh>
        ];
    };

    /**
     * Generate table body dynamically based on data and search filter.
     * @returns {Object} { body: JSX.Element[], data: Array } - table rows and filtered data
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0)
            return { body: [], data: [] };

        let body = results?.content?.map(item => {
            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={'first-column'}>
                        {(item?.assignedToName + " " + item?.assignedToLastName + " " + (item?.assignedToSecondLastName ? item?.assignedToSecondLastName : "")) || EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {(item?.requestedByName + " " + item?.requestedByLastName + " " + (item?.requestedBySecondLastName ? item?.requestedBySecondLastName : "")) || EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}>{getDateFormat(item?.createdAt, true, DATE_TIME_FORMAT_NAME_MODEL.shortMonth) || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.foodieCafeteriaName || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{getDateFormat(item?.fromTime, true, DATE_TIME_FORMAT_NAME_MODEL.shortMonth) || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{getDateFormat(item?.toTime, true, DATE_TIME_FORMAT_NAME_MODEL.shortMonth) || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            background={statusColor[item?.statusId]}
                            fontColor={statusFontColor[item?.statusId]}
                            borderColor={statusBorderColor[item?.statusId]}
                            legend={t(status[item?.statusId]) || EMPTY_VALUES.DEFAULT}
                            variant={"large"}
                        />
                    </CustomTd>
                </>
            );
        });

        return { body, data: results?.content}
    };

    /**
     * Render action buttons (Details, QR, Cancel, Delete) for a table row.
     * @param {Object} item - Table row data
     * @param {number} index - Row index
     * @returns {JSX.Element} DropDown menu with actions
     */
    const getActionsButtons = (item, index) => {
        return <>
            <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${item.id}`}>
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t("details")}
                    icon={<DetailsIconsOptions />}
                    click={() => setItemSelected({ action: ACTION_MODEL.READ, item })}
                />
                {item.statusId === STATUS_MODEL.ACTIVE &&
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t('QR')}
                    icon={<QrCodeIcon />}
                    click={() => handleDownload(item.id)}
                />
                }
                {item.statusId === STATUS_MODEL.ACTIVE && (DEV || permission?.includes(permissionObj?.WEB_FOODIE_ACCESS_CANCEL)) &&
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t("cancel")}
                    fontColor='font-color-error-600'
                    iconColor={"icon-color-error-code-600"}
                    icon={<CancelIconsOptions />}
                    click={() => setItemSelected({ action: ACTION_MODEL.CANCEL, item })}
                />
                }
                {(DEV || permission?.includes(permissionObj?.WEB_FOODIE_ACCESS_DELETE)) &&
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t("delete")}
                    fontColor='font-color-error-600'
                    iconColor={"icon-color-error-code-600"}
                    icon={<DeleteIconsOptions />}
                    click={() => setItemSelected({ action: ACTION_MODEL.DELETE, item })}
                />
                }
            </DropDownMenuProfile>
        </>;
    };

    /**
     * Download the QR code for a specific access code.
     * @async
     * @param {string|number} id - Access code ID
     */
    const handleDownload = async (id) => {
        const { payload } = await dispatch(GetAccessCodeQR(id));
        const { data: responseData, success } = payload.data;
        if (!success) return;
        const qrData = responseData ? JSON.stringify(responseData) : null;
        try {
            const image = await QRCode.toDataURL(qrData, { width: 300 });
            const link = document.createElement('a');
            link.href = image;
            link.download = t("qr_code") + '.png';
            link.click();
        } catch (err) {
            toast.error(t('something_went_wrong_2'));
        }
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page: page, size: rowsPerPage, model: option, key: searchElement, view: view });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true) {
                setResults(data);
            }
        });
    }

    /**
     * Pagination and table functionalities.
     */
    const {
        t,
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        openMenu,
        handleMenuToggle,
        handleClose,
        pagination,
        page,
        rowsPerPage
    } = TableFunction({
        selectedList: selectForDelete,
        setSelectedList: setSelectForDelete,
        data: results,
        view,
        labelRowsPerPage: 'access_codes_by_page',
        getTbody,
        getThead,
        setIsAllChecked,
        fixedPagination: false,
        hasContainer: true,

        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Opens the delete, cancel, or summary modal when an item is selected.
     */
    useEffect(() => {
        if(itemSelected){
            if(itemSelected.action === ACTION_MODEL.DELETE) handleDelete(itemSelected.item);
            else if(itemSelected.action === ACTION_MODEL.CANCEL) handleCancel(itemSelected.item);
            else if(itemSelected.action === ACTION_MODEL.READ) handleSummary(itemSelected.item);
        }
    }, [itemSelected]);

    /**
     * Opens the create modal when `paramsContext.modalCreate` changes.
     */
    useEffect(() => {
        if (paramsContext?.tabIndex === TABS_FOODIE.SCHEDULES.INDEX) {
            setShowAddModal(paramsContext?.modalCreate);
        }
    }, [paramsContext?.modalCreate]);

    /**
     * Opens the summary modal when `paramsContext.modalSummary` changes.
     */
    useEffect(() => {
        if (paramsContext?.tabIndex === TABS_FOODIE.SCHEDULES.INDEX) {
            setShowSummaryModal(paramsContext?.modalSummary);
        }
    }, [paramsContext?.modalSummary]);

    /**
     * Shows the delete modal for a given item.
     * @param {Object} item - Access code item to delete.
     */
    const handleDelete = (item) => {
        setSelectForDelete([item.id]);
        setParamsContext(prev => ({
            ...prev,
            modalDelete: true
        }));
    };

    /**
     * Shows the cancel modal for a given item.
     * @param {Object} item - Access code item to cancel.
     */
    const handleCancel = (item) => {
        setSelectForCancel([item.id]);
        setParamsContext(prev => ({
            ...prev,
            modalCancel: true
        }));
    };

    /**
     * Hides the create modal.
     */
    const handleAddModalClose = () => {
        if (setParamsContext) {
            setParamsContext(prev => ({
                ...prev,
                modalCreate: false
            }));
        }
    };

    /**
     * Hides the summary modal.
     */
    const handleSummaryModalClose = () => {
        if (setParamsContext) {
            setParamsContext(prev => ({
                ...prev,
                modalSummary: false
            }));
        }
    };

    /**
     * Opens the create modal.
     */
    const handleCreate = async () => {
        setParamsContext(prev => ({
            ...prev,
            modalCreate: true
        }));
    };

    /**
     * Opens the summary modal.
     */
    const handleSummary = () => {
        setParamsContext(prev => ({
            ...prev,
            modalSummary: true
        }));
    };

    return {
        t,
        tableId,
        searchId,
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        getActionsButtons,
        pagination,
        isAllChecked,
        showAddModal,
        showSummaryModal,
        handleAddModalClose,
        handleSummaryModalClose,
        handleCreate,
        setBodyForExport,
        setResults,
        setSearchElement,
        page,
        rowsPerPage,
        setSimpleSearch,
        search,
        setSearch,
        searchElement,
        deletePermission,
        itemSelected
    };
};

export default AccessCodeTableApp;
